import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { estadoExportacionDirecto, exportarCasosDirecto } from '../api/api-dashboard-directo'
import { estadoExportacionIntercia, exportarCasosIntercia } from '../api/api-dashboard-intercia'
import { ampliaQuery, buildQuery, buildQueryDirecto, buildQueryEstado, descargaUrl } from './dashboard-utils'

/**
 * RG-602 — Descarga en Excel de los casos que hay detrás de un indicador del tablero.
 *
 * El Excel se genera en segundo plano en la lambda y se sube a S3: aquí se inicia el trabajo, se
 * consulta su estado cada pocos segundos y, cuando está listo, se descarga con la URL firmada que
 * devuelve la lambda. El "cargando" (`exportando`) dura todo ese tiempo.
 *
 * 🔒 Se exporta con la query con que se cargó el resumen que el usuario está viendo (`queryResumen`),
 *    no con los filtros del panel: estos se guardan en Redux al editarlos, antes de pulsar Buscar, y
 *    el Excel no coincidiría con el número de la tarjeta.
 *
 * @param {'directo'|'intercia'} tablero
 * @returns {{ exportar: Function, exportando: boolean, aviso: object|null, cierraAviso: Function }}
 *   `aviso` es `{ code, message }` (1 = ok, 11 = atención, -1 = error), la forma que usa `Mensajes`.
 */
const TABLEROS = {
    directo: {
        iniciar: exportarCasosDirecto,
        estado: estadoExportacionDirecto,
        queryAplicada: (state) => state.dashboardDirectoReducer.queryResumen,
        queryBase: () => buildQueryDirecto({})
    },
    intercia: {
        iniciar: exportarCasosIntercia,
        estado: estadoExportacionIntercia,
        queryAplicada: (state) => state.dashboardInterciaReducer.queryResumen,
        queryBase: () => buildQuery({})
    }
}

const INTERVALO_MS = 2000
const ESPERA_MAX_MS = 15 * 60 * 1000
const FALLAS_SEGUIDAS_MAX = 3

const dormir = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/** Error cuyo texto se muestra tal cual al usuario. */
const errorDeUsuario = (mensaje) => Object.assign(new Error(mensaje), { mensajeUsuario: mensaje })

/**
 * Texto del aviso cuando el Excel quedó generado y se inicia su descarga. Algunas tarjetas suman siniestros WSI (pagos GYE sin caso en OGRD):
 * el Excel trae los casos en la hoja principal y esos siniestros en la hoja «WSI sin caso», y el
 * aviso explica por qué el total de la tarjeta es mayor que los casos.
 */
const mensajeDescarga = ({ cantidad = 0, cantidadWsi = 0 }) => {
    if (!cantidadWsi) return `Se generó Excel con ${cantidad} caso(s).`
    return `Se generó Excel con ${cantidad} caso(s) de OGRD y ${cantidadWsi} siniestro(s) WSI sin caso, `
        + `en la hoja «WSI sin caso». La tarjeta suma ambos: ${cantidad + cantidadWsi}.`
}

/** Sondea el estado hasta que el trabajo termina. Devuelve el resultado LISTO o lanza el error a mostrar. */
const esperaResultado = async (consultaEstado, jobId, sigueActivo) => {
    const query = buildQueryEstado(jobId)
    const limite = Date.now() + ESPERA_MAX_MS
    let fallas = 0

    while (Date.now() < limite && sigueActivo()) {
        await dormir(INTERVALO_MS)
        let estado
        try {
            estado = await consultaEstado(query)
            fallas = 0
        } catch (error) {
            // Un corte de red momentáneo no debe perder un trabajo que sigue corriendo en el servidor.
            fallas += 1
            if (fallas >= FALLAS_SEGUIDAS_MAX) throw error
            continue
        }
        if (estado.estado === 'LISTO') return estado
        if (estado.estado === 'ERROR') throw errorDeUsuario(estado.mensaje || 'No fue posible generar el Excel.')
    }
    if (!sigueActivo()) return null
    throw errorDeUsuario('La generación está tardando más de lo esperado. Intente de nuevo o acote el período.')
}

export const useExportarCasos = (tablero) => {
    const config = TABLEROS[tablero]
    const queryAplicada = useSelector(config.queryAplicada)
    const [exportando, setExportando] = useState(false)
    const [aviso, setAviso] = useState(null)
    const enCurso = useRef(false)
    const montado = useRef(true)

    useEffect(() => {
        montado.current = true
        return () => { montado.current = false }
    }, [])

    /**
     * @param {string} concepto Identificador del indicador (ver `src/conceptos.js` de la lambda).
     * @param {object} [extras] Recorte: `periodo`, `responsableId`, `resultado`, `ciaId`, `motivoId`.
     */
    const exportar = useCallback(async (concepto, extras = {}) => {
        if (enCurso.current) return
        enCurso.current = true
        setExportando(true)
        try {
            const query = ampliaQuery(queryAplicada || config.queryBase(), { concepto, ...extras })
            const inicio = await config.iniciar(query)
            if (!inicio || !inicio.jobId) {
                setAviso({ code: 11, message: 'No hay casos para este indicador con los filtros aplicados.' })
                return
            }

            const resultado = await esperaResultado(config.estado, inicio.jobId, () => montado.current)
            if (!resultado) return // la pantalla se cerró mientras se generaba
            descargaUrl(resultado.url)
            setAviso({ code: 1, message: mensajeDescarga(resultado) })
        } catch (error) {
            const mensaje = error?.response?.data?.error || error?.mensajeUsuario || 'No fue posible exportar los casos.'
            // Un volumen excesivo no es una falla: el usuario debe acotar el período → alerta (naranja), no error.
            const esAlerta = error?.response?.status === 413 || /acote el período/i.test(mensaje)
            if (montado.current) setAviso({ code: esAlerta ? 11 : -1, message: mensaje })
        } finally {
            enCurso.current = false
            if (montado.current) setExportando(false)
        }
    }, [config, queryAplicada])

    return { exportar, exportando, aviso, cierraAviso: () => setAviso(null) }
}
